import { mapKeys, pick, toString, upperFirst } from 'lodash';
import { ActionTypes, PedlActions } from './constants';
import { AnalyticsTypes } from '../../middleware/google-analytics';
import * as TrackingConstants from '../../EventTrackingConstants';
import { GenderFilterEnum } from '../constants';

export const trackDoctorCardClick = () => ({
    type: ActionTypes.TRACK_DOCTOR_CARD_CLICK,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Doctor Card',
            data: {
                Component: TrackingConstants.TARGETS.INFO,
                Element: TrackingConstants.TARGETS.INFO_DOCTOR_CARD,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});

// only additional criteria currently rendered is showProvidersWhoSeeChildren
export const trackFilterClickAdditionalCriteria = ({ showProvidersWhoSeeChildren }) => ({
    type: ActionTypes.TRACK_FILTER_ADDITIONAL_CRITERIA_CLICK,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Additional Filters Dropdown',
            label: showProvidersWhoSeeChildren ? 'Checked Sees Children' : 'Unchecked Sees Children',
            data: {
                Component: TrackingConstants.TARGETS.MORE_FILTERS_DROPDOWN,
                Element: TrackingConstants.TARGETS.ADDITIONAL_CRITERIA_DROPDOWN,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
            },
        },
    },
});

export const trackFilterClickLanguage = ({ languageId }) => ({
    type: ActionTypes.TRACK_FILTER_LANGUAGE_CLICK,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Languages Dropdown',
            label: languageId,
            data: {
                Component: TrackingConstants.TARGETS.MORE_FILTERS_DROPDOWN,
                Element: TrackingConstants.TARGETS.LANGUAGE_DROPDOWN,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
            },
        },
    },
});

export const trackFilterClickSortMatches = ({ sortType }) => ({
    type: ActionTypes.TRACK_FILTER_SORT_MATCHES_CLICK,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Sort Matches Dropdown',
            label: sortType,
            data: {
                Component: TrackingConstants.TARGETS.MORE_FILTERS_DROPDOWN,
                Element: TrackingConstants.TARGETS.SORT_MATCHES_DROPDOWN,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
            },
        },
    },
});

export const trackFilterClickSpecialHours = update => dispatch => {
    let hourSelection;
    if (update.showTimesBefore10 !== undefined) {
        hourSelection = update.showTimesBefore10 ? 'Checked Before 10' : 'Unchecked Before 10';
    } else if (update.showTimesAfter5 !== undefined) {
        hourSelection = update.showTimesAfter5 ? 'Checked After 5' : 'Unchecked After 5';
    }
    dispatch({
        type: ActionTypes.TRACK_FILTER_SPECIAL_HOURS_CLICK,
        meta: {
            analytics: {
                type: AnalyticsTypes.TrackEvent,
                action: 'Click Special Hours Dropdown',
                label: hourSelection,
                data: {
                    Component: TrackingConstants.TARGETS.MORE_FILTERS_DROPDOWN,
                    Element: TrackingConstants.TARGETS.SPECIAL_HOURS_DROPDOWN,
                    Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                    InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                    Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
                },
            },
        },
    });
};

export const trackMapClick = () => ({
    type: ActionTypes.TRACK_MAP_CLICK,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Map',
            data: {
                Component: TrackingConstants.TARGETS.SEARCH_MAP,
                Element: TrackingConstants.TARGETS.MAP,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS,
            },
        },
    },
});

export const trackMobileMapToggleClick = action => ({
    type: ActionTypes.TRACK_MOBILE_MAP_TOGGLE_CLICK,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action,
            data: {
                Component: TrackingConstants.TARGETS.MOBILE_MAP_TOGGLE,
                Element: TrackingConstants.TARGETS.MOBILE_MAP_TOGGLE,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
            },
        },
    },
});

// 'Refine Search' action refers to clicking on the mobile PPS field to expand
// the fields; trackPpsFocus is called when the textbox is actually focused
export const trackMobilePpsClick = () => ({
    type: ActionTypes.TRACK_PPS_CLICK,
    meta: {
        type: AnalyticsTypes.TrackEvent,
        action: 'Refine Search',
        data: {
            Component: TrackingConstants.TARGETS.PATIENT_POWERED_SEARCH,
            Element: TrackingConstants.TARGETS.PATIENT_POWERED_SEARCH_SEARCH_FIELD,
            Initiator: TrackingConstants.EVENT_INITIATORS.USER,
            InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
            Section: TrackingConstants.TARGETS.SEARCH_BAR,
        },
    },
});

export const trackMoreFiltersClick = () => ({
    type: ActionTypes.TRACK_MORE_FILTERS_CLICK,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click More Filters',
            data: {
                Component: TrackingConstants.TARGETS.MORE_FILTERS_BUTTON,
                Element: TrackingConstants.TARGETS.MORE_FILTERS_BUTTON,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS,
            },
        },
    },
});

export const trackPaginationNumberClick = ({ pageNumber }) => dispatch =>
    dispatch({
        type: ActionTypes.TRACK_PAGINATION_NUMBER_CLICK,
        meta: {
            analytics: {
                type: AnalyticsTypes.TrackEvent,
                action: 'Click Pagination Number',
                label: pageNumber,
                data: {
                    Component: TrackingConstants.TARGETS.PAGE_NUMBER,
                    Element: TrackingConstants.TARGETS.PAGE_NUMBER_PAGE_NUMBER,
                    EventDetails: { PageNumber: pageNumber },
                    Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                    InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                    Section: TrackingConstants.TARGETS.PAGINATION,
                },
            },
        },
    });

export const trackPpsChange = ({ query }) => dispatch =>
    dispatch({
        type: ActionTypes.TRACK_PPS_CHANGE,
        meta: {
            analytics: {
                type: AnalyticsTypes.TrackEvent,
                action: 'Change Doctor/Practice Name',
                label: query,
                data: {
                    Component: TrackingConstants.TARGETS.PATIENT_POWERED_SEARCH,
                    Element: TrackingConstants.TARGETS.PATIENT_POWERED_SEARCH_SEARCH_QUERY,
                    EventDetails: { Query: query },
                    Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                    InteractionType: TrackingConstants.INTERACTION_TYPES.TYPE,
                    Section: TrackingConstants.TARGETS.SEARCH_BAR,
                },
            },
        },
    });

export const trackPpsFocus = () => ({
    type: ActionTypes.TRACK_PPS_FOCUS,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click PatientPoweredSearch TextBox',
            data: {
                Component: TrackingConstants.TARGETS.PATIENT_POWERED_SEARCH,
                Element: TrackingConstants.TARGETS.PATIENT_POWERED_SEARCH_SEARCH_FIELD,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_BAR,
            },
        },
    },
});

export const trackPpsSelect = ({ selection, selectionType }) => dispatch => {
    const eventDetails = {
        Selection: selection,
        SelectionType: selectionType,
    };

    dispatch({
        type: ActionTypes.TRACK_PPS_SELECT,
        meta: {
            analytics: {
                type: AnalyticsTypes.TrackEvent,
                action: 'Select PatientPoweredSearch Option',
                label: eventDetails,
                data: {
                    Component: TrackingConstants.TARGETS.PATIENT_POWERED_SEARCH,
                    Element: TrackingConstants.TARGETS.PATIENT_POWERED_SEARCH_SEARCH_AUTOCOMPLETE_TERM,
                    EventDetails: eventDetails,
                    Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                    InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                    Section: TrackingConstants.TARGETS.SEARCH_BAR,
                },
            },
        },
    });
};

export const trackRefineSearchClickGender = ({ gender }) => dispatch => {
    if (gender === GenderFilterEnum.AnyGender) { return; }

    let element;
    let genderName;

    if (gender === GenderFilterEnum.Male) {
        element = TrackingConstants.TARGETS.GENDER_MALE_FILTER;
        genderName = 'Male';
    } else {
        element = TrackingConstants.TARGETS.GENDER_FEMALE_FILTER;
        genderName = 'Female';
    }

    dispatch({
        type: ActionTypes.TRACK_REFINE_SEARCH_CLICK_GENDER,
        meta: {
            analytics: {
                type: AnalyticsTypes.TrackEvent,
                action: 'Change Gender',
                label: genderName,
                data: {
                    Component: TrackingConstants.TARGETS.GENDER,
                    Element: element,
                    Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                    InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                    Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
                },
            },
        },
    });
};

export const trackRefineSearchClickProcedureDropdown = () => ({
    type: ActionTypes.TRACK_REFINE_SEARCH_CLICK_PROCEDURE_DROPDOWN,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Procedure Dropdown',
            data: {
                Component: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS_VISIT_REASON,
                Element: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS_VISIT_REASON_DROPDOWN,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
            },
        },
    },
});

export const trackRefineSearchSelectProcedure = ({ procedureId }) => dispatch =>
    dispatch({
        type: ActionTypes.TRACK_REFINE_SEARCH_SELECT_PROCEDURE,
        meta: {
            analytics: {
                type: AnalyticsTypes.TrackEvent,
                action: 'Change Procedure',
                label: procedureId,
                data: {
                    Component: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS_VISIT_REASON,
                    Element: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS_VISIT_REASON_NEW_VISIT_REASON,
                    EventDetails: { ProcedureId: procedureId },
                    Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                    InteractionType: TrackingConstants.INTERACTION_TYPES.SELECT,
                    Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
                },
            },
        },
    });

export const trackRefineSearchClickSpecialtiesDropdown = () => ({
    type: ActionTypes.TRACK_REFINE_SEARCH_CLICK_SPECIALTY_DROPDOWN,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Specialty Dropdown',
            data: {
                Component: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS_SPECIALTY,
                Element: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS_SPECIALTY_DROPDOWN,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
            },
        },
    },
});

export const trackRefineSearchSelectSpecialty = ({ specialtyId }) => dispatch =>
    dispatch({
        type: ActionTypes.TRACK_REFINE_SEARCH_SELECT_SPECIALTY,
        meta: {
            analytics: {
                type: AnalyticsTypes.TrackEvent,
                action: 'Change Specialty',
                label: specialtyId,
                data: {
                    Component: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS_SPECIALTY,
                    Element: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS_SPECIALTY_NEW_SPECIALTY,
                    EventDetails: { SpecialtyId: specialtyId },
                    Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                    InteractionType: TrackingConstants.INTERACTION_TYPES.SELECT,
                    Section: TrackingConstants.TARGETS.REFINE_SEARCH_RESULTS,
                },
            },
        },
    });

export const trackReviewsReadMoreToggle = () => ({
    type: ActionTypes.TRACK_REVIEWS_READ_MORE_TOGGLE,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Toggle Read More/Less',
            data: {
                Component: TrackingConstants.TARGETS.PATIENT_REVIEWS,
                Element: TrackingConstants.TARGETS.SHOW_MORE_REVIEWS,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});

export const trackSearch = () => ({
    type: ActionTypes.TRACK_SEARCH,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Performed Search',
            data: {
                Component: TrackingConstants.TARGETS.SEARCH_BUTTON,
                Element: TrackingConstants.TARGETS.SEARCH_BUTTON_SEARCH_SUBMIT_BUTTON,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_BAR,
            },
        },
    },
});

export const trackThemedSearch = () => ({
    type: ActionTypes.TRACK_SEARCH,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Performed Search',
            data: {
                Component: TrackingConstants.TARGETS.THEMED_SEARCH,
                Element: TrackingConstants.TARGETS.THEMED_SEARCH,
                Initiator: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.THEMED_SEARCH,
            },
        },
    },
});

export const trackThemedSearchClick = pedlAction => dispatch => {
    let component;
    switch (pedlAction) {
        case PedlActions.THEMED_SEARCH_ALLERGIST_CLICK:
            component = TrackingConstants.TARGETS.THEMED_SEARCH_ALLERGIST;
            break;
        case PedlActions.THEMED_SEARCH_PEDIATRICIAN_CLICK:
            component = TrackingConstants.TARGETS.THEMED_SEARCH_PEDIATRICIAN;
            break;
        case PedlActions.THEMED_SEARCH_PHYSICAL_CLICK:
            component = TrackingConstants.TARGETS.THEMED_SEARCH_PHYSICAL;
            break;
        case PedlActions.THEMED_SEARCH_THERAPIST_CLICK:
            component = TrackingConstants.TARGETS.THEMED_SEARCH_THERAPIST;
            break;
        default:
            pedlAction = undefined;
            break;
    }

    if (pedlAction) {
        dispatch({
            type: ActionTypes.TRACK_THEMED_SEARCH_CLICK,
            meta: {
                analytics: {
                    type: AnalyticsTypes.TrackEvent,
                    action: pedlAction,
                    category: 'Search',
                    data: {
                        Component: component,
                        Element: component,
                        Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                        InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                        Section: TrackingConstants.TARGETS.THEMED_SEARCH,
                    },
                },
            },
        });
    }
};

export const trackTimesgridClickMoreTimes = () => ({
    type: ActionTypes.TRACK_TIMESGRID_CLICK_MORE_TIMES,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click More Times',
            data: {
                Component: TrackingConstants.TARGETS.TIMESGRID,
                Element: TrackingConstants.TARGETS.TIMESGRID_MORE_TIMES,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});

export const trackTimesgridClickNextAvailable = () => ({
    type: ActionTypes.TRACK_TIMESGRID_CLICK_NEXT_AVAILABLE,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Next Availability',
            data: {
                Component: TrackingConstants.TARGETS.TIMESGRID,
                Element: TrackingConstants.TARGETS.TIMESGRID_NEXT_AVAILABLE,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});

export const trackTimesgridClickNext = () => ({
    type: ActionTypes.TRACK_TIMESGRID_CLICK_NEXT_DAYS,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Clicked Next Days',
            data: {
                Component: TrackingConstants.TARGETS.TIMESGRID,
                Element: TrackingConstants.TARGETS.TIMESGRID_NEXT_DAYS_ARROW,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});

export const trackTimesgridClickPrev = () => ({
    type: ActionTypes.TRACK_TIMESGRID_CLICK_PREV_DAYS,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Prev Days',
            data: {
                Component: TrackingConstants.TARGETS.TIMESGRID,
                Element: TrackingConstants.TARGETS.TIMESGRID_PREV_DAYS_ARROW,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});

export const trackTimesgridClickTime = () => ({
    type: ActionTypes.TRACK_TIMESGRID_CLICK_TIME_SLOT,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Time Slot',
            data: {
                Component: TrackingConstants.TARGETS.TIMESGRID,
                Element: TrackingConstants.TARGETS.TIMESGRID_TIMESLOT,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});

export const trackTimesgridClickPhoneNumber = () => ({
    type: ActionTypes.TRACK_TIMESGRID_CLICK_PHONE_NUMBER,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Click Provider Phone Number',
            data: {
                Component: TrackingConstants.TARGETS.TIMESGRID,
                Element: TrackingConstants.TARGETS.TIMESGRID_PROVIDER_PHONE,
                Initiator: TrackingConstants.EVENT_INITIATORS.USER,
                InteractionType: TrackingConstants.INTERACTION_TYPES.CLICK,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});

export const trackTimesgridLoaded = tgState => ({
    type: ActionTypes.TRACK_TIMESGRID_LOADED,
    meta: {
        analytics: {
            type: AnalyticsTypes.TrackEvent,
            action: 'Timesgrid Loaded',
            label: toString(tgState.isExistingPatient),
            data: {
                Component: TrackingConstants.TARGETS.TIMESGRID,
                Element: TrackingConstants.TARGETS.TIMESGRID,
                EventDetails: pick(mapKeys(tgState, (val, key) => upperFirst(key)), [
                    'DirectoryId',
                    'DoctorLocationId',
                    'InsuranceCarrier',
                    'InsurancePlan',
                    'IsExistingPatient',
                    'ProcedureId',
                    'Widget',
                ]),
                Initiator: TrackingConstants.EVENT_INITIATORS.SYSTEM,
                InteractionType: TrackingConstants.INTERACTION_TYPES.UPDATE,
                Section: TrackingConstants.TARGETS.SEARCH_RESULTS_CARD,
            },
        },
    },
});
